import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND } from "../data/content";

const BRAND_LETTERS = "ADIBAH".split("");

export default function SplashScreen({ onFinish }) {
    const [stage, setStage] = useState(0); // 0 mount, 1 letters, 2 fadeout
    const [show, setShow] = useState(true);

    useEffect(() => {
        const hasSeen = sessionStorage.getItem("sra_splash_seen");
        if (hasSeen) {
            setShow(false);
            onFinish?.();
            return;
        }

        // Lock body scroll while splash is active
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const t1 = setTimeout(() => setStage(1), 1300);
        const t2 = setTimeout(() => setStage(2), 3000);
        const t3 = setTimeout(() => {
            setShow(false);
            sessionStorage.setItem("sra_splash_seen", "1");
            document.body.style.overflow = prevOverflow;
            onFinish?.();
        }, 3700);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            document.body.style.overflow = prevOverflow;
        };
    }, [onFinish]);

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    data-testid="splash-screen"
                    initial={{ opacity: 1 }}
                    exit={{
                        opacity: 0,
                        transition: { duration: 0.9, ease: [0.65, 0, 0.35, 1] },
                    }}
                    className="fixed inset-0 z-[200] bg-[#0a0908] overflow-hidden flex items-center justify-center"
                >
                    {/* Curtain reveal panels — split open on exit */}
                    <motion.div
                        initial={{ x: 0 }}
                        animate={
                            stage === 2 ? { x: "-100%" } : { x: 0 }
                        }
                        transition={{
                            duration: 1.1,
                            ease: [0.85, 0, 0.15, 1],
                            delay: 0.15,
                        }}
                        className="absolute inset-y-0 left-0 w-1/2 bg-[#0a0908] z-20 border-r border-[#d4af37]/20"
                    />
                    <motion.div
                        initial={{ x: 0 }}
                        animate={
                            stage === 2 ? { x: "100%" } : { x: 0 }
                        }
                        transition={{
                            duration: 1.1,
                            ease: [0.85, 0, 0.15, 1],
                            delay: 0.15,
                        }}
                        className="absolute inset-y-0 right-0 w-1/2 bg-[#0a0908] z-20"
                    />

                    {/* Subtle radial gold glow */}
                    <div
                        className="absolute inset-0 pointer-events-none z-10 opacity-50"
                        style={{
                            background:
                                "radial-gradient(circle at 50% 50%, rgba(212,175,55,0.18) 0%, transparent 55%)",
                        }}
                    />

                    {/* Decorative gold lines — sweep in from sides */}
                    <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4af37]/40 to-transparent origin-center z-20"
                    />

                    {/* Center content */}
                    <div className="relative z-30 flex flex-col items-center text-center px-6">
                        {/* Logo with rotating gold ring */}
                        <motion.div
                            initial={{ scale: 0.6, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{
                                duration: 1.2,
                                ease: [0.22, 1, 0.36, 1],
                                delay: 0.1,
                            }}
                            className="relative mb-8"
                        >
                            {/* Outer animated ring */}
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{
                                    duration: 24,
                                    repeat: Infinity,
                                    ease: "linear",
                                }}
                                className="absolute -inset-6 sm:-inset-10 rounded-full pointer-events-none"
                                style={{
                                    background:
                                        "conic-gradient(from 0deg, transparent 0deg, #d4af37 35deg, transparent 70deg, transparent 180deg, #d4af37 215deg, transparent 250deg)",
                                    WebkitMask:
                                        "radial-gradient(farthest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1.5px))",
                                    mask: "radial-gradient(farthest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1.5px))",
                                }}
                            />
                            {/* Static thin ring */}
                            <div className="absolute -inset-6 sm:-inset-10 rounded-full border border-[#d4af37]/15 pointer-events-none" />

                            {/* Inner shimmer pulse */}
                            <motion.div
                                animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.08, 1] }}
                                transition={{
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                                className="absolute inset-0 rounded-full pointer-events-none"
                                style={{
                                    boxShadow:
                                        "0 0 80px 10px rgba(212,175,55,0.35)",
                                }}
                            />

                            <img
                                src={BRAND.logoUrl}
                                alt="Sanggar Rias Adibah"
                                className="relative w-32 sm:w-44 lg:w-52 h-auto select-none"
                                draggable={false}
                            />
                        </motion.div>

                        {/* Eyebrow */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 0.7 }}
                            className="text-[10px] sm:text-xs tracking-[0.5em] uppercase text-[#d4af37] mb-4"
                        >
                            Est. {BRAND.foundedYear}
                        </motion.div>

                        {/* Brand letters reveal */}
                        <div
                            className="flex items-center justify-center mb-3 overflow-hidden"
                            aria-label="Adibah"
                        >
                            {BRAND_LETTERS.map((ch, i) => (
                                <motion.span
                                    key={i}
                                    initial={{ y: 60, opacity: 0 }}
                                    animate={
                                        stage >= 1
                                            ? { y: 0, opacity: 1 }
                                            : { y: 60, opacity: 0 }
                                    }
                                    transition={{
                                        duration: 0.8,
                                        ease: [0.22, 1, 0.36, 1],
                                        delay: 0.05 * i,
                                    }}
                                    className="font-display text-5xl sm:text-7xl lg:text-8xl text-[#fdfbf7] tracking-[0.25em] sm:tracking-[0.3em]"
                                    style={{ marginRight: "0.05em" }}
                                >
                                    {ch}
                                </motion.span>
                            ))}
                        </div>

                        <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            animate={
                                stage >= 1 ? { opacity: 1, y: 0 } : { opacity: 0 }
                            }
                            transition={{ duration: 0.8, delay: 0.5 }}
                            className="font-serif italic text-base sm:text-lg text-[#d4af37]"
                        >
                            Sanggar Rias · Mahakarya Pengantin Nusantara
                        </motion.p>

                        {/* Loading bar */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                            className="mt-12 w-48 sm:w-64 h-px bg-[#d4af37]/15 overflow-hidden"
                        >
                            <motion.div
                                initial={{ x: "-100%" }}
                                animate={{ x: "0%" }}
                                transition={{
                                    duration: 2.6,
                                    ease: [0.65, 0, 0.35, 1],
                                }}
                                className="h-full w-full bg-gradient-to-r from-transparent via-[#d4af37] to-[#f0c950]"
                            />
                        </motion.div>
                    </div>

                    {/* Corner ornaments */}
                    {[
                        "top-8 left-8 border-l border-t",
                        "top-8 right-8 border-r border-t",
                        "bottom-8 left-8 border-l border-b",
                        "bottom-8 right-8 border-r border-b",
                    ].map((cls, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.6 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{
                                duration: 1.1,
                                delay: 0.4 + i * 0.08,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className={`absolute ${cls} w-12 h-12 border-[#d4af37]/60 z-30 pointer-events-none`}
                        />
                    ))}
                </motion.div>
            )}
        </AnimatePresence>
    );
}
